import React, { useRef, useState, useContext, useEffect } from "react";
import { Box, Typography, TextField, Button, useTheme, useMediaQuery } from "@mui/material";
import { themeContext } from "../context";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";

export default function Contact() {
  const theme = useContext(themeContext);
  const darkMode = theme.state.darkMode;

  const themeHook = useTheme();
  const isSmallScreen = useMediaQuery(themeHook.breakpoints.down("md"));

  const form = useRef();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    message: '',
  });

  const [formErrors, setFormErrors] = useState({
    userName: false,
    userEmail: false,
    message: false,
  });

  const [touched, setTouched] = useState({
    userName: false,
    userEmail: false,
    message: false,
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched({
      ...touched,
      [name]: true,
    });
  };

  useEffect(() => {
    const errors = {
      userName: formData.userName.trim() === '',
      userEmail: !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.userEmail),
      message: formData.message.trim() === '',
    };
    setFormErrors(errors);
    setIsFormValid(!Object.values(errors).some(error => error));
  }, [formData]); // Only run when formData changes

  const sendEmail = (e) => {
    e.preventDefault();

    if (isFormValid) {
      toast.success("Request saved successfully");
      setFormData({
        userName: '',
        userEmail: '',
        message: '',
      });
      setTouched({
        userName: false,
        userEmail: false,
        message: false,
      });
      navigate('/');
    }
  };

  return (
    <Box
      id="Contact"
      sx={{
        px: 3,
        display: "flex",
        mt: 4,
        pt: 5,
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 5, md: 5 },
        mb: 10
      }}
    >
      {/* Left Section */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            color: darkMode ? "white" : "var(--black)",
            fontSize: isSmallScreen ? '2.2rem': "2.5rem",
            fontWeight: "bold",
            ml: isSmallScreen ? 0 : 30
          }}
        >
          Get in touch
        </Typography>
        <Typography
          sx={{ color: "var(--orange)", fontSize: isSmallScreen ? '2.4rem' :"3rem", fontWeight: "bold", ml: isSmallScreen ? 0 : 30 }}
        >
          Contact Me
        </Typography>
        <Box
          sx={{
            position: "absolute",
            width: "200px",
            height: "200px",
            background: "#ABF1FF94",
            borderRadius: "50%",
            top: "10rem",
            left: "5rem",
            filter: "blur(80px)",
            zIndex: -1,
          }}
        ></Box>
      </Box>

      {/* Right Section */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <form
          ref={form}
          onSubmit={sendEmail}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 15,
            alignItems: "center",
          }}
        >
          <TextField
            name="userName"
            fullWidth
            variant="outlined"
            label="Name"
            value={formData.userName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={touched.userName && formErrors.userName}
            helperText={touched.userName && formErrors.userName && "Name is required."}
            sx={{
              width: { xs: "16rem", md: "20rem" },
              border: "2px solid var(--orange)",
              borderRadius: "8px",
            }}
          />
          <TextField
            name="userEmail"
            type="email"
            fullWidth
            variant="outlined"
            label="Email"
            value={formData.userEmail}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={touched.userEmail && formErrors.userEmail}
            helperText={touched.userEmail && formErrors.userEmail && "Invalid email."}
            sx={{
              width: { xs: "16rem", md: "20rem" },
              border: "2px solid var(--orange)",
              borderRadius: "8px",
            }}
          />
          <TextField
            name="message"
            fullWidth
            variant="outlined"
            multiline
            minRows={4}
            placeholder="Message"
            value={formData.message}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={touched.message && formErrors.message}
            helperText={touched.message && formErrors.message && "Message is required."}
            sx={{
              width: { xs: "16rem", md: "20rem" },
              border: "2px solid var(--orange)",
              borderRadius: "8px",
            }}
          />
          <Button
            type="submit"
            variant="contained"
            sx={{
              ml: 6,
              mr: 4,
              color: "white",
              borderRadius: "20px",
              p: 1,
              pr: 3,
              pl: 3,
              textTransform: "none",
              fontWeight: "bold",
              background: isFormValid ? "" :'grey'
            }}
            disabled={!isFormValid} // Disable button based on form validity
            className="button"
          >
            Contact
          </Button>
          <Box
            sx={{
              position: "absolute",
              width: "200px",
              height: "200px",
              background: "var(--purple)",
              borderRadius: "50%",
              top: "1rem",
              left: "12rem",
              filter: "blur(80px)",
              zIndex: -1,
            }}
          ></Box>
        </form>
      </Box>
    </Box>
  );
}